import { useCallback, useEffect, useState } from 'react';
import {
  App,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Alert,
} from 'antd';
import { PlusOutlined, ReloadOutlined, KeyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api, ApiClient } from '../../services/api';

const TYPE_OPTIONS = [
  { value: 'image', label: '图片' },
  { value: 'video', label: '视频' },
  { value: 'sfx', label: '音效' },
  { value: 'bgm', label: 'BGM' },
];

export default function ClientsPage() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ApiClient[]>([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [secretOnce, setSecretOnce] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listClients({ page: 1, page_size: 100 });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    const values = await form.validateFields();
    try {
      const res = await api.createClient({
        name: values.name,
        allowed_types: (values.allowed_types || []).join(','),
        rate_limit: values.rate_limit || 0,
        remark: values.remark || '',
      });
      setSecretOnce(res.secret_key || null);
      message.success('已创建，请立即保存 SecretKey');
      setOpen(false);
      form.resetFields();
      load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '创建失败');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Typography.Title level={3} className="tech-title" style={{ margin: 0, color: '#fff' }}>
            OpenAPI 凭证
          </Typography.Title>
          <Typography.Text type="secondary">
            HMAC-SHA256 签名协议 · AccessKey / SecretKey
          </Typography.Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({
                allowed_types: ['image', 'video', 'sfx', 'bgm'],
                rate_limit: 0,
              });
              setOpen(true);
            }}
          >
            新建客户端
          </Button>
        </Space>
      </div>

      {secretOnce && (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setSecretOnce(null)}
          style={{ marginBottom: 16 }}
          message="SecretKey 仅显示一次，请妥善保存"
          description={<Typography.Paragraph copyable>{secretOnce}</Typography.Paragraph>}
        />
      )}

      <div className="tech-panel" style={{ padding: 8 }}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          pagination={false}
          columns={[
            { title: '名称', dataIndex: 'name' },
            {
              title: 'AccessKey',
              dataIndex: 'access_key',
              render: (v: string) => <Typography.Text copyable>{v}</Typography.Text>,
            },
            {
              title: '允许类型',
              dataIndex: 'allowed_types',
              render: (v: string) =>
                (v || '').split(',').map((t) => (
                  <Tag key={t}>{TYPE_OPTIONS.find((o) => o.value === t)?.label || t}</Tag>
                )),
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: (v: string) => (
                <Tag color={v === 'active' ? 'success' : 'default'}>{v === 'active' ? '启用' : '停用'}</Tag>
              ),
            },
            {
              title: '限流',
              dataIndex: 'rate_limit',
              width: 100,
              render: (v: number) => (v > 0 ? `${v}/min` : '默认'),
            },
            {
              title: '创建时间',
              dataIndex: 'created_at',
              width: 170,
              render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
            },
            {
              title: '操作',
              width: 220,
              render: (_: unknown, row: ApiClient) => (
                <Space>
                  <Button
                    type="link"
                    icon={<KeyOutlined />}
                    onClick={async () => {
                      try {
                        const res = await api.updateClient(row.id, { rotate_secret: true });
                        setSecretOnce(res.secret_key || null);
                        message.success('已轮换密钥');
                        load();
                      } catch (e) {
                        message.error(e instanceof Error ? e.message : '操作失败');
                      }
                    }}
                  >
                    轮换密钥
                  </Button>
                  <Button
                    type="link"
                    onClick={async () => {
                      const next = row.status === 'active' ? 'disabled' : 'active';
                      try {
                        await api.updateClient(row.id, { status: next });
                        message.success('已更新状态');
                        load();
                      } catch (e) {
                        message.error(e instanceof Error ? e.message : '操作失败');
                      }
                    }}
                  >
                    {row.status === 'active' ? '停用' : '启用'}
                  </Button>
                  <Popconfirm
                    title="确认删除？"
                    onConfirm={async () => {
                      try {
                        await api.deleteClient(row.id);
                        message.success('已删除');
                        load();
                      } catch (e) {
                        message.error(e instanceof Error ? e.message : '删除失败');
                      }
                    }}
                  >
                    <Button type="link" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
        <div style={{ padding: '8px 12px', color: 'var(--text-dim)' }}>共 {total} 个客户端</div>
      </div>

      <Modal title="新建 OpenAPI 客户端" open={open} onCancel={() => setOpen(false)} onOk={create} destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input placeholder="例如：剪辑客户端" />
          </Form.Item>
          <Form.Item name="allowed_types" label="允许资源类型" rules={[{ required: true }]}>
            <Select mode="multiple" options={TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="rate_limit" label="每分钟限流（0=全局默认）">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
